import React from "react";
import {
  IconUser,
  IconLogout2,
  IconMenu2,
  IconArrowNarrowLeft,
  IconSettings,
  IconChevronDown,
} from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../store";
import { NotificationPanel } from "../../../components/elements/form/NavigationPanel";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { logout } from "../../../service/api/auth";

type INavigationBar = {
  onSidebar: () => void;
};

const NavigationBar: React.FC<INavigationBar> = ({ onSidebar }) => {
  const location = useLocation();
  const user = useAppSelector(({ auth }) => auth.user);
  const navbar = useAppSelector(({ navbar }) => navbar);
  const navigate = useNavigate();
  const [showPopover, setShowPopover] = React.useState(false);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery("(max-width: 768px)");

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setShowPopover(false);
      }
    };

    if (showPopover) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopover]);

  const avatar = user?.profile?.avatar;

  const handleSettings = () => {
    navigate("/dashboard/settings");
    setShowPopover(false);
  };

  const handleLogout = () => {
    setShowPopover(false);
    logout(navigate, {
      refresh_token: localStorage.getItem("@refresh_token") ?? "",
      all: true,
    });
  };

  return (
    <header className="h-16 flex justify-between items-center bg-white px-6 border-b border-gray-100/10">
      {/* Left Section */}
      <div className="flex items-center gap-2">
        {location.pathname !== "/dashboard" && (
          <button
            onClick={() => navigate(-1)}
            className="p-1 rounded-lg text-primary-500 hover:bg-gray-100 transition-colors hidden sm:block"
          >
            <IconArrowNarrowLeft size={26} />
          </button>
        )}
        <button
          onClick={onSidebar}
          className="p-1 rounded-lg text-primary-500 hover:bg-gray-100 transition-colors sm:hidden"
        >
          <IconMenu2 size={24} />
        </button>

        <h1 className="text-base lg:text-lg font-semibold text-gray-800 ml-1">
          {navbar?.title
            ? isMobile && String(navbar.title).length > 13
              ? String(navbar.title).slice(0, 13) + "..."
              : navbar.title
            : ""}
        </h1>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        <NotificationPanel />

        {/* User Menu Trigger */}
        <div className="relative" ref={popoverRef}>
          <button
            onClick={() => setShowPopover(!showPopover)}
            className="flex items-center gap-2.5 p-1.5 rounded-xl bg-primary-50/10 hover:bg-gray-100/50 transition-colors focus:outline-none"
          >
            {avatar ? (
              <img
                src={avatar}
                alt="Profile"
                className="h-8 w-8 rounded-lg object-cover ring-1 ring-gray-200"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                <IconUser className="h-4 w-4" />
              </div>
            )}

            <div className="hidden md:flex flex-col items-start text-left leading-tight">
              <span className="text-xs font-semibold text-gray-800">
                {user?.profile?.name || "User"}
              </span>
              <span className="text-[11px] text-gray-500">
                {user?.email
                  ? user.email.length > 15
                    ? `${user.email.slice(0, 15)}...`
                    : user.email
                  : ""}
              </span>
            </div>

            <IconChevronDown
              size={16}
              className="hidden md:flex text-gray-500 ml-1"
            />
          </button>

          {/* User Popover Menu */}
          <div
            className={`absolute right-0 mt-2 w-52 rounded-xl bg-white p-1 shadow-lg border border-gray-100/10 transition-all duration-150 origin-top-right z-50 ${
              showPopover
                ? "opacity-100 scale-100 pointer-events-auto"
                : "opacity-0 scale-95 pointer-events-none"
            }`}
          >
            <button
              onClick={handleSettings}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              <IconSettings size={18} className="text-gray-500" />
              <span>Settings</span>
            </button>

            <div className="my-1 h-px bg-gray-100/10" />

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <IconLogout2 size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default NavigationBar;
